import React, { useState, useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { ConfigProvider, Alert } from 'antd';

import store, { history } from 'APP_ROOT/store';
import { antdLocale } from 'APP_ROOT/i18next';

import PreviewForm from './PreviewForm';
import FileChooser from './FileChooser';
import seedTemplate from './seedTemplate';
import ErrorBoundary from './ErrorBoundary';
import { isEmbedMode, initEmbedBridge } from './embed';

export default function App() {
  const embed = isEmbedMode();
  const [loaded, setLoaded] = useState(null); // template view: { name }
  const [error, setError] = useState(null);
  // Bump on each load so the Form.create-wrapped PreviewForm fully remounts
  // (resets cached redux-form field state) when a new template is chosen.
  const [genKey, setGenKey] = useState(0);

  const onTemplate = useCallback(async (template, fileName) => {
    try {
      if (typeof window !== 'undefined') window.__lastRenderError = null;
      setError(null);
      // Await: getFormPresentation is async, so a malformed template can fail
      // during the transform — catch that here rather than leaking a rejection.
      await seedTemplate(template);
      setLoaded({ name: fileName });
      setGenKey(k => k + 1);
    } catch (e) {
      setError(`Failed to render "${fileName}": ${e.message}`);
    }
  }, []);

  // Dev/test convenience: load by URL (used by automated checks that sweep
  // many files without driving the native file chooser).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__store = store;
    window.__loadTemplate = onTemplate;
    window.__loadTemplateUrl = async url => {
      const res = await fetch(url);
      const json = await res.json();
      const name = url.split('/').pop();
      await onTemplate(json, name);
      return true;
    };
    if (embed) {
      return initEmbedBridge({ onData: (obj) => onTemplate(obj, 'embedded-template') });
    }
  }, [onTemplate]);

  return (
    <ConfigProvider locale={antdLocale}>
      <Provider store={store}>
        <Router history={history}>
          <div className="viewer-shell">
            {!embed ? (
              <header className="viewer-bar">
                <span className="viewer-title">LiveForm Preview</span>
                <FileChooser onTemplate={onTemplate} onError={setError} />
                {loaded ? (
                  <span className="viewer-file" data-testid="loaded-file">
                    {loaded.name}
                  </span>
                ) : null}
              </header>
            ) : null}

            {error ? (
              <div className="viewer-error">
                <Alert type="error" message={error} showIcon />
              </div>
            ) : null}

            <main className="viewer-main">
              {loaded ? (
                <div className="print-frame bdm-form-viewer">
                  <ErrorBoundary key={genKey}>
                    <PreviewForm />
                  </ErrorBoundary>
                </div>
              ) : embed ? (
                <div className="viewer-embed-loading">Loading form…</div>
              ) : (
                <div className="viewer-empty" data-testid="viewer-empty">
                  Choose a template JSON to preview it as a live form.
                </div>
              )}
            </main>
          </div>
        </Router>
      </Provider>
    </ConfigProvider>
  );
}
